// src/views/admin/caisse/ADCaisseConvocations.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../../services/adminService";
import { Loader } from "../../../components/Loader";
import { useToastHook } from "../../../utils/useToast";
import { usePermissions } from "../../../context/PermissionsContext";

const TAGS_CONVOCATION = [
  "{nom_eleve}", "{prenom_eleve}", "{classe}", "{matricule}",
  "{montant_du}", "{montant_paye}", "{reste_a_payer}",
  "{date_echeance}", "{tranche}", "{date_jour}",
];

const MODELE_DEFAUT =
`Cher parent de {prenom_eleve} {nom_eleve} ({classe}),

Nous vous informons qu'un solde de {reste_a_payer} FCFA reste dû au titre de la scolarité, échéance {tranche} du {date_echeance}.

Merci de bien vouloir régulariser cette situation dans les meilleurs délais.

Fait le {date_jour}.`;

/* ============================================================================
 * CONVOCATIONS DE FRAIS
 * Point d'entrée exporté par défaut, importé par ADCaisse.jsx
 *
 * PERMISSIONS (module "caisse") :
 * - caisse.gerer_convocations : accès à cet onglet dans son ensemble
 *   (génération + impression). Déjà filtré en amont dans ADCaisse.jsx —
 *   l'onglet n'est même pas monté si l'utilisateur ne l'a pas — mais le
 *   composant reste défensif au cas où il serait rendu directement.

 * ========================================================================== */
export default function Convocations({ slug }) {
  const { canSee } = usePermissions();
  const peutGererConvocations = canSee("caisse.gerer_convocations");

  const [annees, setAnnees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [classeId, setClasseId] = useState("");
  const [filtreNonAJour, setFiltreNonAJour] = useState(true);
  const [modeleTexte, setModeleTexte] = useState(MODELE_DEFAUT);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [resultats, setResultats] = useState(null);

  const toast = useToastHook();
  const { getAnneesScolaires, getClasses, genererConvocations } = useAdminService();

  useEffect(() => { if (slug && peutGererConvocations) init(); }, [slug, peutGererConvocations]);

  const init = async () => {
    setLoading(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find(a => a.actif);
      const id = String((anneeActive || anneesData[0])?.id || "");
      setAnneeFilt(id);
      if (id) setClasses(await getClasses(slug, { annee_scolaire: id, show_archived: "false" }));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const changerAnnee = async (id) => {
    setAnneeFilt(id);
    setClasseId("");
    try {
      setClasses(await getClasses(slug, { annee_scolaire: id, show_archived: "false" }));
    } catch (error) {
      toast.error("Impossible de charger les classes");
    }
  };

  const handleGenerer = async () => {
    if (!classeId) { toast.error("Sélectionnez une classe"); return; }
    if (!modeleTexte.trim()) { toast.error("Le modèle de texte est requis"); return; }
    setGenerating(true);
    try {
      const data = await genererConvocations(slug, {
        classe_id: classeId,
        filtre_non_a_jour: filtreNonAJour,
        modele_texte: modeleTexte,
      });
      setResultats(data);
      if (data.count === 0) toast.info("Aucun élève ne correspond aux critères");
      else toast.success(`${data.count} convocation(s) générée(s)`);
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.non_field_errors?.[0] || data?.modele_texte?.[0] || "Impossible de générer les convocations");
    } finally {
      setGenerating(false);
    }
  };

  const handleImprimer = () => {
    const fenetre = window.open("", "_blank");
    const contenu = resultats.convocations.map(c =>
      `<div style="page-break-after: always; padding: 30px; font-family: sans-serif; white-space: pre-wrap;">${c.texte.replace(/</g, "&lt;")}</div>`
    ).join("");
    fenetre.document.write(`<html><head><title>Convocations</title></head><body>${contenu}</body></html>`);
    fenetre.document.close();
    fenetre.focus();
    fenetre.print();
  };

  if (!peutGererConvocations) {
    return (
      <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
        Vous n'avez pas accès aux convocations de frais.
      </div>
    );
  }

  if (loading) return <Loader />;

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div className="fgrid">
            <div className="fg">
              <label>Année scolaire</label>
              <select value={anneeFilt} onChange={e => changerAnnee(e.target.value)}>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Classe *</label>
              <select value={classeId} onChange={e => setClasseId(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={filtreNonAJour} onChange={e => setFiltreNonAJour(e.target.checked)} />
            Ne convoquer que les élèves non à jour
          </label>
          <div className="fg">
            <label>Modèle de texte *</label>
            <textarea rows={7} value={modeleTexte} onChange={e => setModeleTexte(e.target.value)} style={{ minHeight: 140, fontFamily: "inherit" }} />
            <div style={{ fontSize: 12, color: "var(--h)", marginTop: 4 }}>
              Tags disponibles : {TAGS_CONVOCATION.map(t => <code key={t} style={{ marginRight: 6 }}>{t}</code>)}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="bprim" onClick={handleGenerer} disabled={generating}>
              {generating ? "Génération..." : <><i className="ti ti-mail" /> Générer les convocations</>}
            </button>
          </div>
        </div>
      </div>

      {resultats && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="ch" style={{ padding: "12px 14px 0" }}>
            <div className="ct">{resultats.count} convocation(s) générée(s)</div>
            {resultats.count > 0 && (
              <button className="bsec" onClick={handleImprimer}><i className="ti ti-printer" />Imprimer tout</button>
            )}
          </div>
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {resultats.convocations.map(c => (
              <div key={c.inscription_id} className="card" style={{ background: "var(--s2)" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{c.eleve}</div>
                <div style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "var(--m)" }}>{c.texte}</div>
              </div>
            ))}
            {resultats.count === 0 && (
              <div style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun élève ne correspond aux critères</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}